<template>
  <div class="class-health-overview">
    <div class="page-header">
      <h2>班级健康概览</h2>
    </div>
    
    <!-- 统计数据卡片 -->
    <div class="stats-container">
      <div class="stat-card">
        <div class="stat-title">总人数</div>
        <div class="stat-value">{{ totalStudents }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-title">平均成绩</div>
        <div class="stat-value">{{ averageScore }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-title">平均成绩等级</div>
        <div class="stat-value">{{ averageGradeLevel }}</div>
      </div>
    </div>
    
    <!-- 图表容器 -->
    <div class="charts-container">
      <div class="chart-wrapper">
        <div id="trendChart" class="chart"></div>
      </div>
      <div class="chart-wrapper">
        <div id="scoreDistributionChart" class="chart"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import * as echarts from 'echarts';
import axios from 'axios';

// 统计数据
const totalStudents = ref(0);
const averageScore = ref('0.00');
const averageGradeLevel = ref('暂无数据');

// 图表实例
const trendChart = ref(null);
const scoreDistributionChart = ref(null);

// 获取成绩等级
const calculateGradeLevel = (score) => {
  if (score >= 90) return '优秀';
  if (score >= 80) return '良好';
  if (score >= 70) return '中等';
  if (score >= 60) return '及格';
  return '不及格';
};

// 获取班级健康数据 - 调用后端API
const fetchClassHealthData = async () => {
  try {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    
    // 设置axios请求头
    const headers = {};
    if (token) {
      headers['Authorization'] = token;
    }
    
    // 获取班级健康概览数据 - 不使用teacherId参数，通过token识别教师身份
    const overviewResponse = await axios.get('/teacher/class-health/overview', {
      headers
    });
    const overviewData = overviewResponse.data.data;
    
    // 更新统计数据
    totalStudents.value = overviewData.totalStudents || 0;
    averageScore.value = overviewData.averageScore ? overviewData.averageScore.toFixed(2) : '0.00';
    averageGradeLevel.value = overviewData.averageGradeLevel || '暂无数据';
    
    // 获取成绩趋势数据 - 不使用teacherId参数，通过token识别教师身份
    const trendResponse = await axios.get('/teacher/class-health/trend', {
      headers
    });
    const trendData = trendResponse.data.data || [];
    
    // 获取成绩分布数据 - 不使用teacherId参数，通过token识别教师身份
    const distributionResponse = await axios.get('/teacher/class-health/distribution', {
      headers
    });
    const scoreDistribution = distributionResponse.data.data || [];
    
    // 更新图表
    nextTick(() => {
      updateTrendChart(trendData);
      updateScoreDistributionChart(scoreDistribution);
    });
  } catch (error) {
    console.error('获取班级健康数据失败:', error);
    // 移除alert提示，只保留控制台日志
    
    // 提供兜底的mock数据，确保页面仍能正常显示
    const fallbackData = {
      totalStudents: 0,
      averageScore: 0,
      averageGradeLevel: '暂无数据',
      trendData: [],
      scoreDistribution: [
        { name: '优秀(90-100)', value: 0 },
        { name: '良好(80-89)', value: 0 },
        { name: '及格(60-79)', value: 0 },
        { name: '不及格(0-59)', value: 0 }
      ]
    };
    
    totalStudents.value = fallbackData.totalStudents;
    averageScore.value = fallbackData.averageScore.toFixed(2);
    averageGradeLevel.value = fallbackData.averageGradeLevel;
    
    nextTick(() => {
      updateTrendChart(fallbackData.trendData);
      updateScoreDistributionChart(fallbackData.scoreDistribution);
    });
  }
};

// 更新趋势图表
const updateTrendChart = (trendData) => {
  if (!trendChart.value) {
    const chartDom = document.getElementById('trendChart');
    if (chartDom) {
      trendChart.value = echarts.init(chartDom);
    } else {
      console.error('找不到趋势图DOM元素');
      return;
    }
  }

  // 确保trendData是数组且不为空
  const safeTrendData = Array.isArray(trendData) ? trendData : [];
  
  // 适配可能的不同字段名，或使用默认值
  const xAxisData = safeTrendData.map(item => item.semester || '未知学期');
  const seriesData = safeTrendData.map(item => item.averageScore || item.passRate || 0);
  
  const option = {
    title: {
      text: '班级体测成绩趋势'
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const data = params[0];
        return `<div>${data.axisValue}</div><div>平均分: ${data.value}</div>`;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData,
      axisLabel: {
        rotate: 30
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: '分数'
    },
    series: [
      {
        name: '平均分',
        type: 'line',
        data: seriesData,
        smooth: true,
        lineStyle: {
          color: '#1890ff',
          width: 2
        },
        areaStyle: {
          opacity: 0.1
        },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  };

  trendChart.value.setOption(option);
};

// 更新成绩分布图表
const updateScoreDistributionChart = (scoreDistribution) => {
  if (!scoreDistributionChart.value) {
    const chartDom = document.getElementById('scoreDistributionChart');
    if (chartDom) {
      scoreDistributionChart.value = echarts.init(chartDom);
    } else {
      console.error('找不到成绩分布图DOM元素');
      return;
    }
  }

  // 确保distributionData是数组且不为空
  const safeDistributionData = Array.isArray(scoreDistribution) && scoreDistribution.length > 0 
    ? scoreDistribution 
    : [
        { name: '优秀(90-100)', value: 0 },
        { name: '良好(80-89)', value: 0 },
        { name: '及格(60-79)', value: 0 },
        { name: '不及格(0-59)', value: 0 }
      ];
  
  // 设置饼图的颜色，保持一致性
  const pieColors = ['#52c41a', '#faad14', '#1890ff', '#ff4d4f'];

  const option = {
    title: {
      text: '成绩分布统计'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      data: safeDistributionData.map(item => item.name)
    },
    series: [
      {
        name: '成绩分布',
        type: 'pie',
        radius: '50%',
        center: ['50%', '60%'],
        data: safeDistributionData,
        color: pieColors,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };

  scoreDistributionChart.value.setOption(option);
};

// 页面加载时获取数据
onMounted(() => {
  // 等待DOM渲染完成后初始化图表
  nextTick(() => {
    fetchClassHealthData();
  });
  
  // 窗口大小变化时重置图表
  window.addEventListener('resize', () => {
    if (trendChart.value) trendChart.value.resize();
    if (scoreDistributionChart.value) scoreDistributionChart.value.resize();
  });
});
</script>

<style scoped>
.class-health-overview {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #333;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #52c41a;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
}

.chart-wrapper {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart {
  width: 100%;
  height: 500px;
}

@media (max-width: 768px) {
  .charts-container {
    grid-template-columns: 1fr;
  }
  
  .chart {
    height: 450px;
  }
}
</style>